import { getAddress, isAddress, type Address } from 'viem';
import { z } from 'zod';
import {
  investorAggregateEnvelopeSchema,
  investorMetricWindowSchema,
  investorScopeSchema,
  servedMessageSchema
} from '@/lib/investor-analytics';
import { usdCents } from '@/lib/format/usd/cents';
import { netOfPendingTwin } from '@/lib/net-of-pending';
import { reportFallback } from '@/lib/report-fallback';
import { tokenSymbolField } from '@/lib/token-symbol';
import { countedRows } from '@/lib/tolerant-rows';
import { vocabulary } from '@/lib/vocabulary';

const finiteNumber = z.number().finite();
const nonNegativeNumber = finiteNumber.nonnegative();
const isoDate = z.union([z.iso.date(), z.iso.datetime({ offset: true })]);
const nullableIsoDate = isoDate.nullable();
const boundedLabel = z.string().trim().min(1).max(256);
const basisPoints = finiteNumber;
const nullableBasisPoints = basisPoints.nullable();
/**
 * Basis points that take part in an identity check. The contract serves
 * these as whole numbers (the daily return series is the one documented
 * exception), and holding them to integers keeps every identity below in
 * exact arithmetic instead of comparing floats.
 */
const wholeBasisPoints = finiteNumber.int();
const nullableWholeBasisPoints = wholeBasisPoints.nullable();

const addressSchema = z
  .string()
  .refine(isAddress, 'Invalid EVM address')
  .transform((value) => getAddress(value));
const nullableAddressSchema = addressSchema.nullable();
/**
 * A strategy currency: a contract address on the wire, validated as one and
 * kept lowercase so lookups by address (rates, `supportedTokens`, the asset
 * registry) compare like for like.
 */
const currencyAddressSchema = addressSchema.transform(
  (address) => address.toLowerCase() as Address
);
const nullableCurrencyAddressSchema = currencyAddressSchema.nullable();
const httpsUrlSchema = z
  .url()
  .refine(
    (value) => new URL(value).protocol === 'https:',
    'HTTPS URL required'
  );
const nullableHttpsUrlSchema = httpsUrlSchema.nullable();
const decimalString = z
  .string()
  .max(128)
  .regex(/^\d+(?:\.\d+)?$/, 'Invalid decimal string');
const nullableDecimalString = decimalString.nullable();
const nullableSignedDecimalString = z
  .string()
  .max(128)
  .regex(/^-?\d+(?:\.\d+)?$/, 'Invalid signed decimal string')
  .nullable();
const rawBalanceString = z.string().max(256).regex(/^\d+$/, 'Invalid balance');
const nullableMetadataText = z.string().max(512).nullable();

const daysToLiquidateSchema = z.object({
  days: nonNegativeNumber,
  label: boundedLabel,
  path: vocabulary(['instant-dex', 'unstake', 'mixed']),
  typicalDays: nonNegativeNumber.nullable().optional(),
  worstCaseDays: nonNegativeNumber.nullable().optional()
});

const investorMetricsHistorySchema = z.object({
  availableDays: nonNegativeNumber.int(),
  requiredDaysSharpe: nonNegativeNumber.int(),
  // v1.47.0 — the money-weighted gate is on the RESOLVED windowDays.
  requiredDaysMoneyWeighted: nonNegativeNumber.int(),
  requiredDaysMaxDd: nonNegativeNumber.int(),
  /**
   * v1.59.0 — the risk-ratio gate moved off account age and onto the
   * REQUESTED window: sharpe/sortino/volatility are null exactly when
   * `usableReturnsInWindow < requiredUsableReturns`, whatever the account's
   * total history. A year-old account still gets nulls on 7d.
   *
   * Optional so a 1.58.0 backend still parses — the pair is read together
   * or not at all, and the copy falls back to the account-age wording when
   * it is absent.
   */
  usableReturnsInWindow: nonNegativeNumber.int().optional(),
  requiredUsableReturns: nonNegativeNumber.int().optional()
});

const metricProvenanceSchema = z.object({
  metricId: boundedLabel,
  formulaVersion: boundedLabel,
  windowDays: nonNegativeNumber.int()
});

/**
 * API 1.159.0: the window return with the fees still building up taken
 * off as well, beside `windowReturnBps`, which stays the figure the app
 * computes with (see `netOfPendingTwin`).
 */
const windowReturnNetOfPending = netOfPendingTwin('investor metrics', {
  value: 'windowReturnNetOfPendingBps',
  reason: 'windowReturnNetOfPendingReason'
});

export const investorRiskMetricsResponseSchema = investorScopeSchema
  .extend({
    asOf: z.iso.datetime({ offset: true }),
    window: investorMetricWindowSchema,
    windowFrom: nullableIsoDate,
    windowTo: nullableIsoDate,
    windowDays: nonNegativeNumber.int(),
    totalValue: nonNegativeNumber,
    investedCapital: nonNegativeNumber,
    ytdPerformanceBps: nullableBasisPoints,
    maxDrawdown12mBps: nonNegativeNumber.nullable(),
    windowReturnBps: nullableBasisPoints,
    /**
     * v1.47.0 — annualized XIRR over the investor's own dated flows. Null
     * under 30 resolved window days, with no or one-sided flows, or when the
     * IRR is mathematically non-unique (the backend never picks a root).
     */
    moneyWeightedReturnBps: nullableBasisPoints,
    sharpeBps: nullableBasisPoints,
    sortinoBps: nullableBasisPoints,
    volatilityBps: nullableBasisPoints,
    daysToLiquidate: daysToLiquidateSchema.nullable(),
    history: investorMetricsHistorySchema,
    provenance: z.array(metricProvenanceSchema),
    ...windowReturnNetOfPending.shape
  })
  .transform(windowReturnNetOfPending.read);

export type InvestorRiskMetrics = z.infer<
  typeof investorRiskMetricsResponseSchema
>;

export const investorTransactionsQuerySchema = z.object({
  page: z.number().int().min(1),
  limit: z.number().int().min(1).max(100)
});

export type InvestorTransactionsQuery = z.infer<
  typeof investorTransactionsQuerySchema
>;

const transactionHashSchema = z
  .string()
  .regex(/^0x[0-9a-fA-F]{64}$/, 'Invalid transaction hash');

const investorTransactionResponseSchema = z.object({
  id: boundedLabel,
  type: vocabulary(['Buy', 'Sell', 'Yield', 'Deposit', 'Withdraw']),
  asset: boundedLabel,
  assetSubLabel: boundedLabel.nullable().optional(),
  amount: finiteNumber,
  date: z.iso.datetime({ offset: true }),
  status: vocabulary(['Completed', 'Pending', 'Failed']),
  counterparty: z.string().trim().min(1).max(256).nullable().optional(),
  txHash: transactionHashSchema.nullable().optional()
});

export const investorTransactionsResponseSchema = investorScopeSchema
  .extend({
    data: z.array(investorTransactionResponseSchema),
    total: nonNegativeNumber.int(),
    page: nonNegativeNumber.int().min(1),
    limit: nonNegativeNumber.int().min(1).max(100),
    // Pinned 'rowa-managed' by contract (v1.89.0); optional so a pre-1.89
    // environment still parses.
    populationScope: z.string().max(64).optional()
  })
  .superRefine((response, context) => {
    if (response.data.length > response.limit) {
      context.addIssue({
        code: 'custom',
        path: ['data'],
        message: 'Transaction page exceeds the declared limit'
      });
    }
  });

type InvestorTransactionResponse = z.infer<
  typeof investorTransactionResponseSchema
>;

export interface InvestorTransaction
  extends Omit<
    InvestorTransactionResponse,
    'assetSubLabel' | 'counterparty' | 'txHash'
  > {
  assetSubLabel: string | null;
  counterparty: string | null;
  txHash: string | null;
}

export interface InvestorTransactionsResponse {
  ownerAddress: Address;
  chainId: number;
  data: InvestorTransaction[];
  total: number;
  page: number;
  limit: number;
  populationScope?: string;
}

export function mapInvestorTransactions(
  response: z.infer<typeof investorTransactionsResponseSchema>
): InvestorTransactionsResponse {
  return {
    ...response,
    data: response.data.map((transaction) => ({
      ...transaction,
      assetSubLabel: transaction.assetSubLabel ?? null,
      counterparty: transaction.counterparty ?? null,
      txHash: transaction.txHash ?? null
    }))
  };
}

const investorHoldingKindSchema = z.enum(['rowa', 'external', 'cash']);

const investorHoldingsSortSchema = z.enum([
  'value',
  'weight',
  'symbol',
  'updatedAt'
]);

export const investorHoldingsQuerySchema = z.object({
  page: z.number().int().min(1).max(1000).optional(),
  limit: z.number().int().min(1).max(100).optional(),
  kind: investorHoldingKindSchema.optional(),
  sort: investorHoldingsSortSchema.optional()
});

export type InvestorHoldingsQuery = z.infer<typeof investorHoldingsQuerySchema>;

const investorHoldingAssetClassSchema = z.object({
  slug: z.string().max(256),
  displayName: nullableMetadataText
});

const investorHoldingIssuerSchema = z.object({
  slug: z.string().max(256),
  name: z.string().max(512)
});

const investorHoldingRiskSchema = z.object({
  auditTier: z.string().max(128),
  centralisationScore: z.string().max(128),
  leverageSource: z.string().max(128)
});

const investorHoldingObjectSchema = z.object({
  source: z.enum(['rowa', 'external']),
  kind: investorHoldingKindSchema,
  chainId: z.number().int().positive(),
  address: nullableAddressSchema,
  symbol: nullableMetadataText,
  /**
   * The token's own `symbol()` (v1.160.0), registry tokens only: an
   * external token's `symbol` is already its provider's ticker.
   */
  tokenSymbol: tokenSymbolField('portfolio', 'investor.holdings.tokenSymbol'),
  name: nullableMetadataText,
  logoUrl: nullableHttpsUrlSchema,
  decimals: z.number().int().min(0).max(255).nullable(),
  balanceRaw: rawBalanceString,
  /**
   * DEPRECATED for `source: 'rowa'` rows (v1.73.0). On those they are
   * strategy-currency amounts carried under a USD name — the same numbers
   * `nativePrice`/`nativeValue` now serve honestly. They stay
   * byte-identical for now, so they remain the fallback until the native
   * fields are confirmed present.
   *
   * On `source: 'external'` rows they are authoritative: third-party tokens
   * are natively priced in USD and need no conversion.
   */
  priceUsd: nullableDecimalString,
  // Unreadable (blank) reads as no source, which the label already handles.
  priceSource: vocabulary(['nav', 'market-data', 'alchemy'])
    .nullable()
    .catch(null),
  valueUsd: nullableDecimalString,
  /**
   * v1.73.0. The amount and its real unit. All three are null on external
   * rows, where USD already is the native currency.
   *
   * `nativeCurrency` is the strategy currency's CONTRACT ADDRESS, lowercase,
   * not a symbol. It keys the lookup in `fx.rates` and in the chain's token
   * set; what the reader sees is resolved from those (`utils/holding-format`).
   */
  nativePrice: nullableDecimalString.optional(),
  nativeValue: nullableDecimalString.optional(),
  // Anything that is not an address cannot be matched to a rate, so the
  // row's currency is unknown: it is left out of dollar figures with that
  // reason, and the rest of the response stands.
  nativeCurrency: nullableCurrencyAddressSchema.optional().catch(null),
  weightBps: finiteNumber.int().min(0).max(10_000).nullable(),
  assetClass: investorHoldingAssetClassSchema.nullable(),
  classificationSource: vocabulary(['registry', 'manual', 'heuristic'])
    .nullable()
    .catch(null),
  issuer: investorHoldingIssuerSchema.nullable(),
  risk: investorHoldingRiskSchema.nullable(),
  liquidityTier: nullableMetadataText,
  updatedAt: z.iso.datetime({ offset: true }).nullable()
});

/**
 * The native trio belongs to ROWA rows only: the contract serves it null on
 * external rows, whose `valueUsd` is authoritative. A stray value there
 * would convert a wallet token through a vault's exchange rate, so it is
 * set aside before validation: never used, and never a reason to reject the
 * response, whatever shape it arrives in.
 */
const investorHoldingSchema = z.preprocess(
  (row) =>
    row != null &&
    typeof row === 'object' &&
    (row as { source?: unknown }).source !== 'rowa'
      ? { ...row, nativePrice: null, nativeValue: null, nativeCurrency: null }
      : row,
  investorHoldingObjectSchema
);

/**
 * Conversion context for the native amounts above (v1.73.0).
 *
 * A currency with no rate appears here with `priceUsd: null` rather than
 * being left out — omission would be indistinguishable from "holds nothing
 * in it". `coverageBps` is weighted by native value rather than by position
 * count, is null (never 0) when there is nothing to cover, and is computed
 * over the unfiltered set, so it does not move with `?kind=` or `?page=`.
 */
const investorHoldingsFxRateSchema = z.object({
  // The strategy currency's CONTRACT ADDRESS. It is the key a row's
  // `nativeCurrency` is matched against, never something to print.
  currency: currencyAddressSchema,
  priceUsd: nullableDecimalString,
  // Provider observation time for the rate; null when there is none.
  observedAt: z.string().max(64).nullish(),
  // The rate is past its freshness bound and was served anyway. A
  // conversion that uses it says so instead of passing as current.
  stale: z.boolean().optional()
});

const investorHoldingsFxSchema = z.object({
  // Parsed row by row: a rate whose currency is not an address can convert
  // nothing, so it is dropped and the others stand. A row that then has no
  // rate is left out of dollar figures with that reason.
  rates: z
    .array(z.unknown())
    .max(1_000)
    .transform((rows) =>
      rows.flatMap((row) => {
        const rate = investorHoldingsFxRateSchema.safeParse(row);
        return rate.success ? [rate.data] : [];
      })
    )
    .optional(),
  coverageBps: nonNegativeNumber.int().max(10_000).nullable().optional(),
  /** True when any rate above is stale. */
  stale: z.boolean().optional()
});

const investorHoldingsTotalsSchema = z.object({
  rowaValueUsd: decimalString,
  externalValueUsd: decimalString,
  cashValueUsd: decimalString,
  costBasisUsd: nullableDecimalString,
  unrealizedPnlUsd: nullableSignedDecimalString,
  coveredValueUsd: decimalString
});

const investorHoldingsCoverageSchema = z
  .object({
    holdingsTotal: nonNegativeNumber.int(),
    pricedCount: nonNegativeNumber.int(),
    // Null means ROWA could not measure the ratio (an empty portfolio is
    // the launch default) — never zero, which would claim nothing is
    // priced. API v1.57.0.
    pricedBps: nonNegativeNumber.int().max(10_000).nullable(),
    classifiedCount: nonNegativeNumber.int(),
    classifiedBps: nonNegativeNumber.int().max(10_000).nullable(),
    externalIncluded: z.boolean(),
    // A status this build has not seen parses and is held to neither rule
    // below: only the known values carry an inclusion contract.
    externalStatus: vocabulary([
      'fresh',
      'stale',
      'absent',
      'failed',
      'disabled'
    ]),
    externalAsOf: z.iso.datetime({ offset: true }).nullable(),
    externalSchemaVersion: nonNegativeNumber.int().nullable()
  })
  .superRefine((coverage, context) => {
    if (coverage.pricedCount > coverage.holdingsTotal) {
      context.addIssue({
        code: 'custom',
        path: ['pricedCount'],
        message: 'Priced count exceeds total holdings'
      });
    }
    if (coverage.classifiedCount > coverage.holdingsTotal) {
      context.addIssue({
        code: 'custom',
        path: ['classifiedCount'],
        message: 'Classified count exceeds total holdings'
      });
    }
    // `stale` legitimately goes either way and the flag is the
    // discriminator: true means the rows ARE served from a current-schema
    // snapshot whose TTL expired (age in externalAsOf, recompute queued),
    // false means old-schema residue that was excluded. Requiring
    // inclusion to imply `fresh` rejected a valid 200 on first connect —
    // the snapshot exists but the recompute has not landed — and took the
    // whole holdings response, and NAV with it, down over metadata.
    const mustBeIncluded = coverage.externalStatus === 'fresh';
    const mustBeExcluded =
      coverage.externalStatus === 'absent' ||
      coverage.externalStatus === 'failed' ||
      coverage.externalStatus === 'disabled';
    if (
      (mustBeIncluded && !coverage.externalIncluded) ||
      (mustBeExcluded && coverage.externalIncluded)
    ) {
      context.addIssue({
        code: 'custom',
        path: ['externalIncluded'],
        message: 'External inclusion does not match external data status'
      });
    }
  });

// Screens read the code and the context; the text is never shown.
const investorHoldingsWarningSchema = z.object({
  code: z.string().max(128),
  message: servedMessageSchema,
  context: z.record(z.string(), z.unknown()).optional()
});

/**
 * The population a holdings response describes (BE-000, shipped in API
 * v1.89.0). Decided PER REQUEST: 'wallet-merged' when the external wallet
 * scan contributed to the searched population (it tracks
 * `coverage.externalIncluded`), 'rowa-managed' otherwise. It names the
 * population SEARCHED, not the rows found — a wallet with zero external
 * tokens still reads 'wallet-merged' (`totals.externalValueUsd` "0.00" with
 * `externalIncluded` true is the tell). Every other investor envelope pins
 * 'rowa-managed'.
 *
 * Typed so consumers see the two known values while an unknown future value
 * still parses; anything other than 'rowa-managed' must be treated as NOT
 * like-for-like with the always-rowa-managed envelopes (/performance, /pnl,
 * /metrics, ...).
 */
type HoldingsPopulationScope = 'rowa-managed' | 'wallet-merged' | (string & {});

const holdingsPopulationScopeSchema = z
  .enum(['rowa-managed', 'wallet-merged'])
  .or(z.string().max(64)) as z.ZodType<HoldingsPopulationScope>;

export const investorHoldingsResponseSchema = investorScopeSchema
  .extend({
    asOf: z.iso.datetime({ offset: true }),
    // The currency `totals` are stated in. api.md: always "USD", and every
    // total is a `*Usd` field (owner, 2026-10-03: portfolio figures are
    // USD). Pinned: a response naming another currency contradicts its own
    // figures, and the total, the shares and the snapshot percentages all
    // derive from them, so it is refused at the boundary (the holdings read
    // shows its error state) rather than shown under the wrong name.
    valuationCurrency: z.literal('USD'),
    page: z.number().int().min(1).max(1000),
    limit: z.number().int().min(1).max(100),
    total: nonNegativeNumber.int(),
    totals: investorHoldingsTotalsSchema,
    coverage: investorHoldingsCoverageSchema,
    // Optional so a pre-1.89 environment still parses. Absence means
    // undeclared, which consumers treat exactly like 'wallet-merged':
    // not like-for-like.
    populationScope: holdingsPopulationScopeSchema.optional(),
    holdings: z.array(investorHoldingSchema),
    warnings: z.array(investorHoldingsWarningSchema),
    fx: investorHoldingsFxSchema.nullish()
  })
  .superRefine((response, context) => {
    if (
      response.holdings.length > response.limit ||
      response.holdings.length > response.total
    ) {
      context.addIssue({
        code: 'custom',
        path: ['holdings'],
        message: 'Holdings page exceeds its declared bounds'
      });
    }
    response.holdings.forEach((holding, index) => {
      if (holding.chainId !== response.chainId) {
        context.addIssue({
          code: 'custom',
          path: ['holdings', index, 'chainId'],
          message: 'Holding chain does not match response scope'
        });
      }
    });
  });

export type InvestorHolding = z.infer<typeof investorHoldingSchema>;
export type InvestorHoldingsResponse = z.infer<
  typeof investorHoldingsResponseSchema
>;

// ---------------------------------------------------------------------------
// P&L, fees, and attribution (backend v1.45.0–v1.47.0). USD figures are
// 2 decimal strings formatted once server side. The roll forward and
// waterfall identities are verified here in integer cents, with the
// tolerance policy from `src/lib/investor-analytics/schemas.ts`: each entry
// is rounded on its own, so a sum may drift by up to one cent (or one basis
// point) per entry. Demanding exactness would take the section down over a
// cent of rounding. A real error is orders of magnitude larger than that
// bound, and a response that misses it is still rejected as unverifiable.
// ---------------------------------------------------------------------------

const usd2 = z
  .string()
  .max(64)
  .regex(/^\d+\.\d{2}$/, 'Invalid USD amount');
const signedUsd2 = z
  .string()
  .max(64)
  .regex(/^-?\d+\.\d{2}$/, 'Invalid signed USD amount');

/** |left − right| cents is within one cent of rounding per summed entry. */
function withinRounding(left: bigint, right: bigint, entries: number): boolean {
  const drift = left > right ? left - right : right - left;
  return drift <= BigInt(entries);
}

/**
 * The bound for an identity the backend DERIVES rather than rounds into.
 * Each of these is computed from the served integers themselves, so it
 * holds exactly whatever the number of entries:
 * - the attribution residual is the window return minus the served
 *   contributions;
 * - each fee total is the sum of its served parts in cents, and the per
 *   vault accruals partition the window total;
 * - `windowExpenseBps` is management + performance + protocol, and
 *   `grossReturnBps` is net + the served drag steps
 *   (investor-fees.service.ts).
 * A bound that grew with the entry count would let a 40 vault book pass with
 * 40 basis points (or 40 cents) unaccounted for. Two units is room for a
 * rounding change upstream and nothing more.
 */
const DERIVED_IDENTITY_SLACK = 2;

/**
 * The same bound for basis points. These are whole numbers by schema, so
 * the subtraction is exact; a refinement can still run on a value the field
 * check has already rejected, which is why this never converts to BigInt.
 */
function withinRoundingBps(
  left: number,
  right: number,
  entries: number
): boolean {
  return Math.abs(left - right) <= entries;
}

const availabilityStatusSchema = z.object({
  available: z.boolean(),
  reason: z.string().max(256),
  plannedBasis: z.string().max(4000).optional()
});

const pnlRollForwardShape = {
  openingValueUsd: usd2,
  closingValueUsd: usd2,
  contributionsUsd: usd2,
  withdrawalsUsd: usd2,
  netFlowUsd: signedUsd2,
  netPnlUsd: signedUsd2
};

function addRollForwardIssues(
  entity: z.infer<z.ZodObject<typeof pnlRollForwardShape>>,
  context: z.RefinementCtx,
  path: (string | number)[]
) {
  if (
    !withinRounding(
      usdCents(entity.netFlowUsd),
      usdCents(entity.contributionsUsd) - usdCents(entity.withdrawalsUsd),
      2
    )
  ) {
    context.addIssue({
      code: 'custom',
      path: [...path, 'netFlowUsd'],
      message: 'Net flow does not equal contributions minus withdrawals'
    });
  }
  if (
    !withinRounding(
      usdCents(entity.closingValueUsd),
      usdCents(entity.openingValueUsd) +
        usdCents(entity.netFlowUsd) +
        usdCents(entity.netPnlUsd),
      3
    )
  ) {
    context.addIssue({
      code: 'custom',
      path: [...path, 'closingValueUsd'],
      message: 'Roll forward identity does not reconcile'
    });
  }
}

/** API 1.159.0: the month's return after fees still building up too. */
const monthReturnNetOfPending = netOfPendingTwin('investor pnl months', {
  value: 'returnNetOfPendingBps',
  reason: 'returnNetOfPendingReason'
});

const pnlMonthSchema = z
  .object({
    month: z.string().regex(/^\d{4}-\d{2}$/, 'Invalid month'),
    from: isoDate,
    to: isoDate,
    ...pnlRollForwardShape,
    returnBps: nullableBasisPoints,
    ...monthReturnNetOfPending.shape
  })
  .transform(monthReturnNetOfPending.read);

export const investorPnlResponseSchema = investorAggregateEnvelopeSchema
  .extend({
    ...pnlRollForwardShape,
    realizedPnlUsd: nullableSignedDecimalString,
    unrealizedPnlUsd: nullableSignedDecimalString,
    feesUsd: nullableDecimalString,
    pnlBreakdownStatus: availabilityStatusSchema,
    provenance: z.array(
      z.object({
        metricId: boundedLabel,
        formulaVersion: boundedLabel,
        windowDays: nonNegativeNumber.int().optional()
      })
    ),
    months: z.array(pnlMonthSchema).max(60).optional(),
    monthsTruncated: z.boolean().optional(),
    monthsTotal: nonNegativeNumber.int().optional()
  })
  .superRefine((response, context) => {
    addRollForwardIssues(response, context, []);
    const months = response.months ?? [];
    months.forEach((month, index) => {
      addRollForwardIssues(month, context, ['months', index]);
      if (
        index > 0 &&
        !withinRounding(
          usdCents(month.openingValueUsd),
          usdCents(months[index - 1]!.closingValueUsd),
          1
        )
      ) {
        context.addIssue({
          code: 'custom',
          path: ['months', index, 'openingValueUsd'],
          message: 'Months do not telescope'
        });
      }
    });
    if (
      months.length > 0 &&
      !withinRounding(
        usdCents(months.at(-1)!.closingValueUsd),
        usdCents(response.closingValueUsd),
        1
      )
    ) {
      context.addIssue({
        code: 'custom',
        path: ['months'],
        message: 'Last month does not close at the window closing value'
      });
    }
  });

export type InvestorPnlResponse = z.infer<typeof investorPnlResponseSchema>;

export const investorPnlQuerySchema = z.object({
  window: investorMetricWindowSchema.optional(),
  granularity: z.enum(['window', 'month']).optional()
});
export type InvestorPnlQuery = z.infer<typeof investorPnlQuerySchema>;

const feeSetSchema = z.object({
  managementUsd: usd2,
  performanceUsd: usd2,
  protocolUsd: usd2,
  curatorUsd: usd2.nullable(),
  entryUsd: usd2,
  exitUsd: usd2,
  totalUsd: usd2
});

// ---------------------------------------------------------------------------
// Pending fees and excluded fee events (API 1.157.0, additive). Pending fees
// are the management and performance fees a vault has built up since its
// last charge and not yet minted. They are information beside the charged
// figures: no total, ratio or identity on this response includes them, and
// nothing on screen is reduced by them. Every field is optional and costs
// only itself when it fails, so the 1.156 body reads exactly as before.
// ---------------------------------------------------------------------------

/**
 * Every field below is optional and costs only itself: absent or null reads
 * as null, silently; a served value of the wrong shape reads as null too and
 * is reported once under its contract name, never with its value.
 */
const servedField = <T extends z.ZodType>(schema: T, contract: string) =>
  schema
    .nullish()
    .catch(reportFallback('portfolio', `investor.fees.${contract}`, null));
/** A ~USD figure: exactly two places, as every charged figure here (`usd2`). */
const pendingUsd = (contract: string) => servedField(usd2, contract);
const pendingTime = (contract: string) =>
  servedField(z.iso.datetime({ offset: true }), contract);

/**
 * One vault's pending fees and this investor's share of them. `state` says
 * what the amounts mean, so a block without one is unread as a whole.
 * `reason` is a backend code and `vaultFeeShares` a raw share count: parsed,
 * never printed.
 */
const positionPendingFeesSchema = z.object({
  state: vocabulary(['measured', 'unmeasured', 'not_held']),
  reason: servedField(z.string().max(64), 'positions.pendingFees.reason'),
  asOf: pendingTime('positions.pendingFees.asOf'),
  pendingSince: pendingTime('positions.pendingFees.pendingSince'),
  managementUsd: pendingUsd('positions.pendingFees.managementUsd'),
  performanceUsd: pendingUsd('positions.pendingFees.performanceUsd'),
  totalUsd: pendingUsd('positions.pendingFees.totalUsd'),
  vaultManagementUsd: pendingUsd('positions.pendingFees.vaultManagementUsd'),
  vaultPerformanceUsd: pendingUsd('positions.pendingFees.vaultPerformanceUsd'),
  vaultTotalUsd: pendingUsd('positions.pendingFees.vaultTotalUsd'),
  vaultFeeShares: servedField(
    rawBalanceString,
    'positions.pendingFees.vaultFeeShares'
  ),
  performanceFeeStatus: servedField(
    vocabulary(['accruing', 'high_water_mark_unset', 'none']),
    'positions.pendingFees.performanceFeeStatus'
  )
});

/**
 * The vaults the backend says it could not read. `served` is whether it
 * listed anything at all: an entry that is not an address still says some
 * vault is unread, and so is a list that is not a list. A summed block always
 * carries the list (empty when every vault was read), so one without it
 * cannot vouch for its total either. `addresses` are the entries that can be
 * named.
 */
function servedUnreadVaults(value: unknown): {
  served: boolean;
  addresses: Address[];
} {
  if (!Array.isArray(value)) return { served: true, addresses: [] };
  return {
    served: value.length > 0,
    addresses: value
      .slice(0, 1_000)
      .flatMap((vault) =>
        typeof vault === 'string' && isAddress(vault, { strict: false })
          ? [getAddress(vault)]
          : []
      )
  };
}

/**
 * The investor's share summed over held vaults. The amounts are null when
 * any held vault could not be read; those vaults are listed, addresses only.
 * That invariant is held here: amounts served beside a non empty list would
 * be a partial sum, so they are read as null, and the slip is reported once.
 */
const investorPendingFeesSchema = z
  .object({
    asOf: pendingTime('pendingFees.asOf'),
    managementUsd: pendingUsd('pendingFees.managementUsd'),
    performanceUsd: pendingUsd('pendingFees.performanceUsd'),
    totalUsd: pendingUsd('pendingFees.totalUsd'),
    // Any served entry, an address or not, says some vault is unread;
    // only the addresses among them can be named.
    unmeasuredVaults: z.unknown().transform(servedUnreadVaults)
  })
  .transform(({ unmeasuredVaults: { served, addresses }, ...pending }) => {
    const partial =
      served &&
      (pending.managementUsd != null ||
        pending.performanceUsd != null ||
        pending.totalUsd != null);
    if (!partial) return { ...pending, unmeasuredVaults: addresses };
    reportFallback(
      'portfolio',
      'investor.fees.pendingFees.partialTotal',
      undefined
    )({
      error: new Error('Pending fee total served beside unmeasured vaults')
    });
    return {
      ...pending,
      unmeasuredVaults: addresses,
      managementUsd: null,
      performanceUsd: null,
      totalUsd: null
    };
  });

/**
 * A fee event that cost the investor money and is in no total. The vault
 * identifies it; everything else is read field by field. The served
 * `message` is backend text: parsed, never shown.
 */
const excludedFeeEventSchema = z.object({
  vaultAddress: addressSchema,
  txHash: servedField(
    z.string().regex(/^0x[0-9a-fA-F]{64}$/),
    'excludedEvents.txHash'
  ),
  blockNumber: servedField(
    z.string().max(32).regex(/^\d+$/),
    'excludedEvents.blockNumber'
  ),
  logIndex: servedField(nonNegativeNumber.int(), 'excludedEvents.logIndex'),
  eventType: servedField(
    vocabulary([
      'MANAGEMENT_FEES_MINTED',
      'PERFORMANCE_FEES_MINTED',
      'DEPOSIT_FEE_COLLECTED',
      'WITHDRAW_FEE_COLLECTED'
    ]),
    'excludedEvents.eventType'
  ),
  blockTimestamp: pendingTime('excludedEvents.blockTimestamp'),
  reason: servedField(
    vocabulary(['ASSET_DECIMALS_UNKNOWN', 'NO_PRE_EVENT_SUPPLY']),
    'excludedEvents.reason'
  ),
  message: servedField(z.string().max(2_000), 'excludedEvents.message')
});

/**
 * An optional block: absent or null reads as absent, silently; a block that
 * fails its parse (not an object, no state) reads as absent too and is
 * reported once under its contract name.
 */
function reportedBlock<T extends z.ZodType>(block: T, contract: string) {
  return block
    .nullish()
    .transform((value) => value ?? undefined)
    .catch(reportFallback('portfolio', `investor.fees.${contract}`, undefined))
    .optional();
}

export type PositionPendingFees = z.infer<typeof positionPendingFeesSchema>;
export type ExcludedFeeEvent = z.infer<typeof excludedFeeEventSchema>;

/**
 * API 1.159.0: the net return after fees still building up too. Kept out
 * of the gross to net walk below, which reconciles charged fees only.
 */
const netReturnNetOfPending = netOfPendingTwin(
  'investor fees',
  {
    value: 'netReturnNetOfPendingBps',
    reason: 'netReturnNetOfPendingReason'
  },
  { value: wholeBasisPoints }
);

export const investorFeesResponseSchema = investorAggregateEnvelopeSchema
  .extend({
    accrued: feeSetSchema,
    accruedMtd: feeSetSchema,
    accruedYtd: feeSetSchema,
    averageNavUsd: usd2,
    windowExpenseBps: nullableWholeBasisPoints,
    annualizedExpenseBps: nullableBasisPoints,
    grossReturnBps: nullableWholeBasisPoints,
    netReturnBps: nullableWholeBasisPoints,
    ...netReturnNetOfPending.shape,
    dragBps: z.object({
      managementBps: nullableWholeBasisPoints,
      performanceBps: nullableWholeBasisPoints,
      protocolBps: nullableWholeBasisPoints,
      curatorBps: nullableWholeBasisPoints
    }),
    positions: z
      .array(
        z.object({
          vaultAddress: addressSchema,
          name: boundedLabel,
          distanceToHwmBps: nullableBasisPoints,
          accruedUsd: usd2,
          pendingFees: reportedBlock(
            positionPendingFeesSchema,
            'positions.pendingFees'
          )
        })
      )
      .max(1_000),
    pendingFees: reportedBlock(investorPendingFeesSchema, 'pendingFees'),
    // Rows parse one at a time; `dropped` counts the ones that could not be
    // placed, so the screen keeps its general note when the list is short.
    // A list that is not a list, or runs past the ceiling, is reported.
    excludedEvents: reportedBlock(
      countedRows(excludedFeeEventSchema, {
        label: 'excluded fee events',
        max: 1_000
      }),
      'excludedEvents'
    ),
    // Machine-readable measurement basis; surfaced fields are read
    // defensively, so unknown additions never break the parse.
    feesBasis: z.record(z.string(), z.unknown()),
    provenance: z.array(
      z.object({
        metricId: boundedLabel,
        formulaVersion: boundedLabel,
        windowDays: nonNegativeNumber.int().optional()
      })
    )
  })
  .superRefine((response, context) => {
    for (const [key, fees] of [
      ['accrued', response.accrued],
      ['accruedMtd', response.accruedMtd],
      ['accruedYtd', response.accruedYtd]
    ] as const) {
      const parts = [
        fees.managementUsd,
        fees.performanceUsd,
        fees.protocolUsd,
        fees.curatorUsd,
        fees.entryUsd,
        fees.exitUsd
      ].filter((part): part is string => part != null);
      const partsCents = parts.reduce((sum, part) => sum + usdCents(part), 0n);
      if (
        !withinRounding(
          partsCents,
          usdCents(fees.totalUsd),
          DERIVED_IDENTITY_SLACK
        )
      ) {
        context.addIssue({
          code: 'custom',
          path: [key, 'totalUsd'],
          message: 'Fee total does not equal the sum of its parts'
        });
      }
    }

    const { managementBps, performanceBps, protocolBps, curatorBps } =
      response.dragBps;
    if (
      response.windowExpenseBps != null &&
      managementBps != null &&
      performanceBps != null &&
      protocolBps != null &&
      !withinRoundingBps(
        response.windowExpenseBps,
        managementBps + performanceBps + protocolBps,
        DERIVED_IDENTITY_SLACK
      )
    ) {
      context.addIssue({
        code: 'custom',
        path: ['windowExpenseBps'],
        message: 'Expense ratio does not equal the sum of drag steps'
      });
    }
    if (response.grossReturnBps != null && response.netReturnBps != null) {
      const steps = [
        managementBps,
        performanceBps,
        protocolBps,
        curatorBps
      ].filter((value): value is number => value != null);
      const dragSum = steps.reduce((sum, value) => sum + value, 0);
      if (
        !withinRoundingBps(
          response.grossReturnBps,
          response.netReturnBps + dragSum,
          DERIVED_IDENTITY_SLACK
        )
      ) {
        context.addIssue({
          code: 'custom',
          path: ['grossReturnBps'],
          message: 'Gross return does not reconcile through the drag steps'
        });
      }
    }

    const positionsCents = response.positions.reduce(
      (sum, position) => sum + usdCents(position.accruedUsd),
      0n
    );
    if (
      !withinRounding(
        positionsCents,
        usdCents(response.accrued.totalUsd),
        DERIVED_IDENTITY_SLACK
      )
    ) {
      context.addIssue({
        code: 'custom',
        path: ['positions'],
        message: 'Per position fees do not sum to the accrued total'
      });
    }
  })
  .transform(netReturnNetOfPending.read);

export type InvestorFeesResponse = z.infer<typeof investorFeesResponseSchema>;

export const investorFeesQuerySchema = z.object({
  window: investorMetricWindowSchema.optional()
});
export type InvestorFeesQuery = z.infer<typeof investorFeesQuerySchema>;

/**
 * API 1.159.0: the portfolio return after fees still building up too. Not
 * broken down by position, so it is outside the contributions identity.
 */
const portfolioReturnNetOfPending = netOfPendingTwin(
  'investor attribution',
  {
    value: 'portfolioReturnNetOfPendingBps',
    reason: 'portfolioReturnNetOfPendingReason'
  },
  { value: wholeBasisPoints }
);

export const investorAttributionResponseSchema = investorAggregateEnvelopeSchema
  .extend({
    attribution: z
      .object({
        byPosition: z
          .array(
            z.object({
              vaultAddress: addressSchema,
              name: boundedLabel,
              contributionBps: wholeBasisPoints
            })
          )
          .max(1_000),
        // Reserved null in v1; typed loosely so the v2 decomposition is
        // additive, per the endpoint's versioning contract.
        bySource: z.unknown().nullable(),
        bySourceStatus: availabilityStatusSchema,
        basis: boundedLabel,
        portfolioReturnBps: wholeBasisPoints,
        residualBps: wholeBasisPoints,
        ...portfolioReturnNetOfPending.shape
      })
      .transform(portfolioReturnNetOfPending.read)
      .nullable(),
    provenance: z.array(
      z.object({
        metricId: boundedLabel,
        formulaVersion: boundedLabel,
        windowDays: nonNegativeNumber.int().optional()
      })
    )
  })
  .superRefine((response, context) => {
    if (response.attribution == null) return;
    const contributionSum = response.attribution.byPosition.reduce(
      (sum, position) => sum + position.contributionBps,
      0
    );
    if (
      !withinRoundingBps(
        contributionSum + response.attribution.residualBps,
        response.attribution.portfolioReturnBps,
        DERIVED_IDENTITY_SLACK
      )
    ) {
      context.addIssue({
        code: 'custom',
        path: ['attribution', 'residualBps'],
        message: 'Contributions plus residual do not equal the window return'
      });
    }
  });

export type InvestorAttribution = NonNullable<
  z.infer<typeof investorAttributionResponseSchema>['attribution']
>;
export type InvestorAttributionResponse = z.infer<
  typeof investorAttributionResponseSchema
>;

export const investorAttributionQuerySchema = z.object({
  window: investorMetricWindowSchema.optional()
});
export type InvestorAttributionQuery = z.infer<
  typeof investorAttributionQuerySchema
>;
